/**
 * Spec Phase 11 — persistent Generate-Everything history pill.
 *
 * Renders next to the "Generate everything" button. Shows a live
 * count of recent runs from the store; click to open a popover
 * listing every run with status, completed/total counts, duration,
 * and timestamp. The list is hydrated by SpecPage on mount and
 * refreshed by the polling loop on every terminal status, so
 * completed runs never disappear.
 *
 * Renders nothing when no runs exist for the active matter.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, History, Loader2, XCircle,
} from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecGenerateEverythingRun } from '@/api/client';

interface Props {
  matterId: string;
}

export default function GenerateEverythingHistory(
  { matterId: _matterId }: Props,
): JSX.Element | null {
  const runs = useSpecStore((s) => s.generateEverythingRuns);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Click outside to dismiss; capture phase so the open button's
  // own click doesn't immediately close the popover that just opened.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const node = popoverRef.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (runs.length === 0) return null;

  const liveCount = runs.filter(
    (r) => r.status === 'queued' || r.status === 'running',
  ).length;

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        className={
          'inline-flex h-9 items-center gap-1.5 rounded-sm border px-2.5 text-sm '
          + 'font-medium transition-colors '
          + (liveCount > 0
            ? 'border-zinc-400 bg-zinc-50 text-zinc-800'
            : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50')
        }
        onClick={() => setOpen((v) => !v)}
        title={
          liveCount > 0
            ? `${liveCount} generation in progress · ${runs.length} total`
            : `${runs.length} past generation${runs.length === 1 ? '' : 's'}`
        }
        data-testid="spec-ge-history-toggle"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {liveCount > 0
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <History className="h-3.5 w-3.5" />}
        <span className="tabular-nums">{runs.length}</span>
      </button>
      {open && (
        <div
          className={
            'absolute right-0 top-full z-30 mt-1 w-80 rounded-md border '
            + 'border-zinc-200 bg-white shadow-lg'
          }
          role="dialog"
          aria-label="Generate everything history"
          data-testid="spec-ge-history-popover"
        >
          <div className="border-b border-zinc-100 px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-zinc-500">
            Recent generations
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {runs.map((r) => (
              <li
                key={r.id}
                className="px-3 py-2 text-xs hover:bg-zinc-50"
                data-testid={`spec-ge-history-run-${r.id}`}
                data-status={r.status}
              >
                <RunSummary run={r} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RunSummary({ run }: { run: SpecGenerateEverythingRun }): JSX.Element {
  const total = run.progress.total_sections ?? 0;
  const ok = run.progress.completed ?? 0;
  const failed = run.progress.failed ?? 0;
  const isLive = run.status === 'queued' || run.status === 'running';
  const icon = isLive
    ? <Loader2 className="h-3 w-3 animate-spin text-zinc-500" />
    : run.status === 'completed' && failed === 0
      ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
      : run.status === 'completed' && failed > 0
        ? <AlertTriangle className="h-3 w-3 text-amber-600" />
        : run.status === 'cancelled'
          ? <XCircle className="h-3 w-3 text-zinc-400" />
          : <AlertTriangle className="h-3 w-3 text-rose-600" />;
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium text-zinc-800 capitalize">{run.status}</span>
          <span className="font-mono text-[10px] text-zinc-400 tabular-nums">
            {_formatTimestamp(run.created_at)}
          </span>
        </div>
        <p className="text-[11px] text-zinc-600">
          {total > 0
            ? `${ok} of ${total} section${total === 1 ? '' : 's'}`
            : 'queued'}
          {failed > 0 ? ` · ${failed} failed` : ''}
          {run.total_duration_ms != null
            ? ` · ${Math.round(run.total_duration_ms / 1000)}s`
            : ''}
        </p>
        {run.error_message && (
          <p className="mt-0.5 text-[11px] text-rose-700 line-clamp-2">
            {run.error_message}
          </p>
        )}
      </div>
    </div>
  );
}

function _formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const isSameDay = d.toDateString() === now.toDateString();
    if (isSameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 16);
  }
}
